import { createApp, Plugin } from "vue";
import App from "./App.vue";
import "@/assets/css/tailwind.css";
import "./registerServiceWorker";
import router from "./router";
import store from "./store";
import socket from "./socket-client";
import VueCookies from "vue-cookies";
import { registerLicense } from "@syncfusion/ej2-base";
import { persistDeepLinkFromLocation, buildReloadUrlKeepingDeepLink } from "@/lib/deepLink";
import { hideBootSplash, isNativeWebView } from "@/lib/AppUtils";

const socketPlugin: Plugin = {
  install: (app) => {
    app.config.globalProperties.$socket = socket;
  },
};

//Syncfusion Licence key
registerLicense("Ngo9BigBOggjHTQxAR8/V1NCaF1cVWhIfEx1RHxQdld5ZFRHallYTnNWUj0eQnxTdEFjUX9WcHJWQmVZUkRyXw==");

// ---------------------------------------------------------------------------
// Build version kill-switch
// ---------------------------------------------------------------------------
// Every build writes public/version.json with the current git SHA (see
// scripts/generate-version.js). On boot we fetch it (nginx serves it
// with no-cache so we always see the deployed build's SHA, not a cached
// one) and compare against the SHA we previously stored in
// localStorage. If they differ, the user is sitting on stale code from
// a prior deploy — we nuke everything and reload so they boot fresh:
//
//   * localStorage.clear()          → drops vuex state, Auth0 cache,
//                                     server JWT, username, etc. → user
//                                     lands on /auth0login.
//   * Do NOT sessionStorage.clear() → hs_notify_deeplink must survive so
//                                     an email /login?redirect* still
//                                     opens /ico after this reload.
//   * caches.delete(*)              → wipes Workbox precache so the new
//                                     index.html / chunks aren't shadowed.
//   * serviceWorker.unregister()    → forces a clean SW install on reload.
//   * location.replace(same path + query + &_v=SHA)
//                                   → cache-busted hard reload that keeps
//                                     the email deep-link query.
//
// Guarded by a sessionStorage flag so we can NEVER loop: if a reload
// fails to apply the new version for any reason, the second boot will
// see the flag and skip the reset (and just boot on whatever the user
// has). This is intentional — preferring "boot stale code once" over
// "infinite reload loop" — the next launch will retry.
//
// Requires: nginx must serve /version.json with `Cache-Control:
// no-cache, must-revalidate`. Without that header this whole mechanism
// silently no-ops because the browser will cache version.json and we'll
// never see the new SHA.
// ---------------------------------------------------------------------------

const VERSION_KEY = "app_version";
const VERSION_RESET_FLAG = "app_version_reset_in_progress";

async function checkBuildVersionAndMaybeReset(): Promise<void> {
  try {
    // Capture /login?redirect* BEFORE any replace. A new-deploy reset
    // used to location.replace("/?_v=sha"), which dropped the email
    // deep-link query and sent a cold login to /property-list. Local
    // webpack never hits this path, which is why ICO worked there.
    persistDeepLinkFromLocation();

    // Bail out if we just reloaded ourselves — prevents any chance of a
    // reload loop if cache-clearing didn't take effect for some reason.
    if (sessionStorage.getItem(VERSION_RESET_FLAG)) {
      sessionStorage.removeItem(VERSION_RESET_FLAG);
      return;
    }

    // `cache: "no-store"` on the request itself is a belt-and-suspenders
    // companion to the nginx no-cache header — if devops hasn't applied
    // it yet, this still works in most browsers (some WebViews ignore
    // it, which is why the nginx header is the real fix).
    const controller = new AbortController();
    const abortTimer = window.setTimeout(() => controller.abort(), 2500);
    let res: Response;
    try {
      res = await fetch("/version.json?t=" + Date.now(), {
        cache: "no-store",
        credentials: "omit",
        signal: controller.signal,
      });
    } finally {
      window.clearTimeout(abortTimer);
    }
    if (!res.ok) return;
    const data = await res.json();
    const serverSha: string = data && data.sha ? String(data.sha) : "";
    if (!serverSha) return;

    const storedSha = localStorage.getItem(VERSION_KEY);

    // Same build — nothing to do, boot normally.
    if (storedSha === serverSha) return;

    // First visit (fresh install / WebView with empty localStorage) is
    // already on this build. location.replace after cache+SW wipe is
    // what leaves the Android WebView on a blank white document:
    // splash is gone, Vue never mounts, and the replacement load
    // often never paints. Just record the SHA and continue.
    if (!storedSha) {
      try {
        localStorage.setItem(VERSION_KEY, serverSha);
      } catch (_e) {
        /* ignore */
      }
      return;
    }

    // Android/iOS WebView: a cache-wipe + location.replace is what
    // blanks the document. Record the new SHA and keep booting so
    // Auth0 can start. The next cold start will already be on this SHA.
    if (isNativeWebView()) {
      try {
        localStorage.setItem(VERSION_KEY, serverSha);
      } catch (_e) {
        /* ignore */
      }
      return;
    }

    // ---- NEW DEPLOY DETECTED ----
    console.warn("[version-check] New build detected, resetting client state", { from: storedSha, to: serverSha });
    sessionStorage.setItem(VERSION_RESET_FLAG, "1");

    try {
      localStorage.clear();
    } catch (_e) {
      /* ignore */
    }
    try {
      // Re-write the new SHA AFTER clearing localStorage so we don't
      // re-trigger the reset on the post-reload boot.
      localStorage.setItem(VERSION_KEY, serverSha);
    } catch (_e) {
      /* ignore */
    }
    try {
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister().catch(() => undefined)));
      }
    } catch (_e) {
      /* ignore */
    }
    try {
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k).catch(() => undefined)));
      }
    } catch (_e) {
      /* ignore */
    }

    // Hard reload, cache-busted. Keep the current path + redirect*
    // query so an email deep link still reaches /ico after the reset.
    window.location.replace(buildReloadUrlKeepingDeepLink(serverSha));
    // Throwing here keeps Vue from mounting on top of the about-to-be-
    // reloaded page. Caller is bootApp() and catches.
    throw new Error("__app_version_reset__");
  } catch (err: any) {
    if (err && err.message === "__app_version_reset__") throw err;
    // Network failure / missing version.json / parse error → do nothing
    // and let the app boot normally. The check will run again next time.
    console.warn("[version-check] skipped:", err);
  }
}

async function bootApp(): Promise<void> {
  if (isNativeWebView() && "serviceWorker" in navigator) {
    try {
      const regs = await Promise.race([
        navigator.serviceWorker.getRegistrations(),
        new Promise<ServiceWorkerRegistration[]>((resolve) => window.setTimeout(() => resolve([]), 500)),
      ]);
      await Promise.all(regs.map((r) => r.unregister().catch(() => undefined)));
    } catch (_e) {
      /* ignore */
    }
  }

  try {
    await Promise.race([
      checkBuildVersionAndMaybeReset(),
      new Promise<void>((resolve) => window.setTimeout(resolve, 2500)),
    ]);
  } catch (err: any) {
    // Version reset is in progress — page is about to reload, do NOT
    // mount Vue (would be wasted work and could briefly flash the
    // wrong screen).
    if (err && err.message === "__app_version_reset__") return;
  }

  const app = createApp(App).use(store).use(router).use(VueCookies).use(socketPlugin);
  app.mount("#app");
  router.isReady().then(() => hideBootSplash());
}

bootApp();
